import { useCallback, useEffect, useMemo, useState } from "react";
import { PriceListLine } from "@forgepoint/shared";
import { Loader, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { usePriceListsStore } from "./priceListsStore";
import PriceListLineEditorSheet from "./PriceListLineEditorSheet";
import { formatPricingUnit } from "@forgepoint/shared";
import { formatUnitPrice } from "@/lib/utils";

const PAGE_SIZE = 50;

interface PriceListLineTableProps {
  priceListId: string;
  /** Whether the user may add, edit, or remove lines; when false the table is read-only. */
  canEdit: boolean;
}

/** Paginated table for viewing and managing price list lines. */
export default function PriceListLineTable({
  priceListId,
  canEdit,
}: PriceListLineTableProps) {
  const {
    priceListLines,
    priceListLineTotals,
    priceListLinesLoadingStates,
    fetchPriceListLines,
    removePriceListLines,
  } = usePriceListsStore();

  const [page, setPage] = useState(0);
  const [currentSearch, setCurrentSearch] = useState("");
  const [editingLine, setEditingLine] = useState<PriceListLine | null>(null);
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);
  const [removingLineId, setRemovingLineId] = useState<string | null>(null);

  const loadingState = priceListLinesLoadingStates.get(priceListId);
  const total = priceListLineTotals.get(priceListId) ?? 0;
  const lineMap = priceListLines.get(priceListId);
  const linesArray = useMemo(
    () => (lineMap ? [...lineMap.values()] : []),
    [lineMap],
  );

  const loadLines = useCallback(
    (pageNum: number, search: string) => {
      fetchPriceListLines(priceListId, {
        search: search || undefined,
        limit: PAGE_SIZE,
        offset: pageNum * PAGE_SIZE,
      });
    },
    [fetchPriceListLines, priceListId],
  );

  useEffect(() => {
    loadLines(0, "");
  }, [loadLines]);

  const handleSearch = useCallback(
    (value: string) => {
      setPage(0);
      setCurrentSearch(value);
      loadLines(0, value);
    },
    [loadLines],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      loadLines(newPage, currentSearch);
    },
    [loadLines, currentSearch],
  );

  const handleRemoveLine = useCallback(
    async (lineId: string) => {
      setRemovingLineId(lineId);
      const removed = await removePriceListLines(priceListId, [lineId]);
      setRemovingLineId(null);

      if (removed) {
        toast.success("Line removed");
        loadLines(page, currentSearch);
      } else {
        toast.error("Failed to remove line");
      }
    },
    [removePriceListLines, priceListId, loadLines, page, currentSearch],
  );

  const handleLinesSaved = useCallback(() => {
    setIsAddSheetOpen(false);
    setEditingLine(null);
    loadLines(page, currentSearch);
  }, [loadLines, page, currentSearch]);

  const formatPriceBreaks = (line: PriceListLine): string => {
    if (line.priceBreaks.length === 0) {
      return "—";
    }
    if (line.priceBreaks.length === 1) {
      return formatUnitPrice(line.priceBreaks[0]!.unitPrice);
    }
    return line.priceBreaks
      .map((pb) => `${pb.minQty}+: ${formatUnitPrice(pb.unitPrice)}`)
      .join(", ");
  };

  const columns: DataTableColumn<PriceListLine>[] = useMemo(() => {
    const baseColumns: DataTableColumn<PriceListLine>[] = [
      {
        key: "item",
        header: "Item",
        type: "string",
        format: (_value, line) => line.item?.name ?? "—",
      },
      {
        key: "itemRevision",
        header: "Rev",
        type: "string",
        width: 60,
        format: (_value, line) => line.item?.revision ?? "—",
      },
      {
        key: "itemType",
        header: "Type",
        type: "badge",
        width: 120,
        format: (_value, line) => line.item?.type ?? "—",
      },
      {
        key: "unit",
        header: "Unit",
        type: "string",
        width: 80,
        format: (_value, line) =>
          line.unit ? formatPricingUnit(line.unit) : "—",
      },
      {
        key: "priceBreaks",
        header: "Price Breaks",
        type: "string",
        format: (_value, line) => formatPriceBreaks(line),
      },
    ];

    if (!canEdit) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        key: "actions",
        header: "",
        width: 60,
        format: (_value, line) => {
          const isRemoving = removingLineId === line.id;
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                handleRemoveLine(line.id);
              }}
              disabled={isRemoving}
            >
              {isRemoving ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          );
        },
      },
    ];
  }, [removingLineId, handleRemoveLine, canEdit]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Lines</h3>
          <p className="text-sm text-muted-foreground">
            {total} item{total !== 1 ? "s" : ""} in this price list
          </p>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAddSheetOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Line
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={linesArray}
        onClickRow={canEdit ? (line) => setEditingLine(line) : undefined}
        onChangeSearch={handleSearch}
        searchPlaceholder="Search by item name..."
        isLoading={loadingState?.isLoading && linesArray.length === 0}
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total,
          onPageChange: handlePageChange,
        }}
      />

      <PriceListLineEditorSheet
        open={isAddSheetOpen}
        onOpenChange={setIsAddSheetOpen}
        priceListId={priceListId}
        onSaved={handleLinesSaved}
      />

      <PriceListLineEditorSheet
        open={!!editingLine}
        onOpenChange={(open) => {
          if (!open) {
            setEditingLine(null);
          }
        }}
        priceListId={priceListId}
        existingLine={editingLine ?? undefined}
        onSaved={handleLinesSaved}
      />
    </div>
  );
}
